<script lang="ts">
  import { type LoginSession } from '@nrk/innlogging-web/redirect-login-client';
  import type { State } from '@pin/schemas/client-state-response';
  import { isFuture } from 'date-fns';
  import { onDestroy, onMount } from 'svelte';
  import {
    clientStateObservable,
    emptyStateId,
  } from '@pin/client-state-observable';
  import AriaLive from './components/AriaLive.svelte';
  import CookieBanner from './components/CookieBanner.svelte';
  import FeedbackButton from './components/Feedback/FeedbackButton.svelte';
  import Footer from './components/Footer.svelte';
  import Loader from './components/Loader.svelte';
  import HeaderLink from './login/HeaderLink.svelte';
  import {
    loginSessionStore,
    loginClient,
    forceRefresh,
  } from './login/loginSessionStore';
  import { readClientStateAriaLive } from './util/clientStateAriaLive';
  import { setUser, trackPageView, trackEvent } from './util/snowplow';
  import After from './views/After.svelte';
  import Before from './views/Before.svelte';
  import Closed from './views/Closed.svelte';
  import Empty from './views/Empty.svelte';
  import Login from './views/Login.svelte';
  import Open from './views/Open.svelte';
  import Poster from './views/Poster.svelte';
  import InnloggingDebug from './components/Debug/InnloggingDebug.svelte';
  import Strekkodelogo from './assets/Strekkodelogo.svelte';
  import P3Logo from './assets/P3-logo.svelte';
  import Wireframe from './components/Three/Wireframe.svelte';
  import { supportsWebGL } from './util/webgl-check';

  const webGLSupported = supportsWebGL();

  onMount(async () => {
    const session = await loginClient.getSession();
    loginSessionStore.set(session);
    if (session.isLoggedIn && !!session.user) {
      setUser(session.user?.sub);
    }

    if (
      (session.isLoggedIn && session.isOffgrid) ||
      (session.isLoggedIn && session.isExpired)
    ) {
      forceRefresh();
    }

    trackPageView();
    loginClient.initOpSession();
    loginClient.addEventListener('update', onSessionUpdate);
  });

  onDestroy(async () => {
    loginClient.removeEventListener('update', onSessionUpdate);
  });

  const onSessionUpdate = (event: CustomEvent<LoginSession>) => {
    const session = event.detail;
    loginSessionStore.set(session);
    if (session.isLoggedIn && !!session.user) {
      setUser(session.user?.sub);
    }
  };

  const checkOpen = (clientState: State | undefined) =>
    clientState?.type == 'open' ||
    (clientState?.type === 'closing' &&
      isFuture(new Date(clientState.endTime)));

  const clientState = clientStateObservable({
    apiUrl: import.meta.env.VITE_CLIENT_API_URL,
    clientId: import.meta.env.VITE_CLIENT_ID,
    onError: () => {
      trackEvent({ action: 'error:fetching-clientstate' });
    },
  });

  $: isOpen = checkOpen($clientState);
  $: readClientStateAriaLive($clientState);
</script>

<InnloggingDebug />
<CookieBanner />
<HeaderLink />
{#if webGLSupported}
  <Wireframe />
{/if}
<div class="p3-logo-container" aria-label="P3-logo">
  <a href="https://p3.no/" target="_blank">
    <P3Logo />
  </a>
</div>
<main>
  <div class="logo-container" aria-label="P3 Gull-logo">
    <Strekkodelogo />
  </div>
  <AriaLive mode="assertive" />
  {#if $clientState == null}
    <Empty />
  {:else if $clientState.id === emptyStateId || $loginSessionStore == null}
    <Loader description="Laster avstemning" />
  {:else if $clientState.type === 'closing' && !isOpen}
    <Closed pollData={$clientState} />
  {:else if $clientState.type === 'after'}
    <After pollData={$clientState} />
  {:else if $clientState.type === 'poster'}
    <Poster pollData={$clientState} />
  {:else if !$loginSessionStore.user}
    <Login />
  {:else if $clientState.type === 'before'}
    <Before pollData={$clientState} />
  {:else if $clientState.type === 'open' || $clientState.type === 'closing'}
    <Open pollData={$clientState} />
  {/if}

  <FeedbackButton />
</main>
<Footer />

<style>
  main {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 0 var(--spacing-16) var(--spacing-32);
  }
  a {
    padding: 0;
  }

  .p3-logo-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 3rem;
    margin-right: 1rem;
    max-width: 90%;
  }

  .logo-container {
    margin-bottom: 3rem;
  }
</style>
